import { LAYER_Z_INDEX } from "@/features/map/constants/layerZIndex";
import {
  GSI_PHOTO_ATTRIBUTION_WITH_GRUS,
  buildGsiTileUrl,
} from "@/features/map/constants/gsiPhotoLayers";
import { GsiPhotoTileLayer } from "./GsiPhotoTileLayer";

/**
 * 国土地理院「全国最新写真（シームレス）」の航空写真レイヤー。
 *
 * 地理院地図の「写真」と同じく、ズームレベルごとに 3 つのソースを分担して 1 レイヤーとして見せる。
 * （地理院タイル一覧 https://maps.gsi.go.jp/development/ichiran.html の「写真」項目に準拠）
 * - 世界衛星モザイク画像      modis          ZL 2〜8  （地図の最小ズーム 7 から使用）
 * - 全国ランドサットモザイク  lndst          ZL 9〜13
 * - 全国最新写真（シームレス）seamlessphoto  ZL 14〜18（19 以上は拡大表示で補う）
 *
 * ベースマップ（OSM / cds地図）より上、地図サービス層（ゼンリン・GEOSPACE 等）
 * より下に重ねるため、zIndex は LAYER_Z_INDEX.SEAMLESS_PHOTO を使用する。
 */
export const SEAMLESS_PHOTO_LAYER_NAME = "全国最新写真（国土地理院）";

export const SEAMLESS_PHOTO_TILE_URL = buildGsiTileUrl("seamlessphoto", "jpg");
export const LANDSAT_MOSAIC_TILE_URL = buildGsiTileUrl("lndst", "png");
export const WORLD_SATELLITE_MOSAIC_TILE_URL = buildGsiTileUrl("modis", "png");

/** 世界衛星モザイク画像の表示開始ズーム（地図全体の最小ズームに合わせる） */
export const WORLD_SATELLITE_MOSAIC_MIN_ZOOM = 7;
/** 世界衛星モザイク画像の配信最大ズーム。ここまでは衛星画像、以降はランドサットへ切り替える。 */
export const WORLD_SATELLITE_MOSAIC_MAX_ZOOM = 8;
/** 全国ランドサットモザイク画像を表示するズーム帯 */
export const LANDSAT_MOSAIC_MIN_ZOOM = 9;
export const LANDSAT_MOSAIC_MAX_ZOOM = 13;
/** 地理院が全国最新写真を配信する最小ズームレベル。 */
export const SEAMLESS_PHOTO_MIN_ZOOM = 14;
/** 地理院が全国最新写真を配信する最大ズームレベル。これを超える範囲は拡大表示で補う。 */
export const SEAMLESS_PHOTO_MAX_NATIVE_ZOOM = 18;
/** 地図全体の最大ズーム（MAX_ZOOM）まで拡大表示を継続する。 */
export const SEAMLESS_PHOTO_MAX_ZOOM = 22;

/**
 * 出典表記はソースごとに分けて各タイルに載せる（地理院タイル一覧の指示に従う）。
 * - 国土地理院リンク＋GRUS は年度別空中写真と同じ定数を使い、両方 ON でも出典欄に 1 回だけ表示させる
 * - 世界衛星モザイク画像は指定文が長いため要約する
 */
const LANDSAT_MOSAIC_ATTRIBUTION =
  "データソース：Landsat8画像（GSI,TSIC,GEO Grid/AIST）, Landsat8画像（courtesy of the U.S. Geological Survey）, 海底地形（GEBCO）";
const WORLD_SATELLITE_MOSAIC_ATTRIBUTION =
  "世界衛星モザイク画像: NASA LP DAAC / USGS EROS Center";

interface Props {
  /** レイヤーコントロールでの表示ON/OFF */
  visible: boolean;
  /** 透過率（0〜1）。未指定なら不透明。 */
  opacity?: number;
}

export const SeamlessPhotoTileLayer: React.FC<Props> = ({
  visible,
  opacity,
}) => {
  return (
    <>
      <GsiPhotoTileLayer
        visible={visible}
        className="seamless-photo-tile-layer"
        attribution={GSI_PHOTO_ATTRIBUTION_WITH_GRUS}
        url={SEAMLESS_PHOTO_TILE_URL}
        minZoom={SEAMLESS_PHOTO_MIN_ZOOM}
        maxNativeZoom={SEAMLESS_PHOTO_MAX_NATIVE_ZOOM}
        maxZoom={SEAMLESS_PHOTO_MAX_ZOOM}
        zIndex={LAYER_Z_INDEX.SEAMLESS_PHOTO}
        opacity={opacity}
      />
      <GsiPhotoTileLayer
        visible={visible}
        className="seamless-photo-tile-layer"
        attribution={LANDSAT_MOSAIC_ATTRIBUTION}
        url={LANDSAT_MOSAIC_TILE_URL}
        minZoom={LANDSAT_MOSAIC_MIN_ZOOM}
        maxNativeZoom={LANDSAT_MOSAIC_MAX_ZOOM}
        maxZoom={LANDSAT_MOSAIC_MAX_ZOOM}
        zIndex={LAYER_Z_INDEX.SEAMLESS_PHOTO}
        opacity={opacity}
      />
      <GsiPhotoTileLayer
        visible={visible}
        className="seamless-photo-tile-layer"
        attribution={WORLD_SATELLITE_MOSAIC_ATTRIBUTION}
        url={WORLD_SATELLITE_MOSAIC_TILE_URL}
        minZoom={WORLD_SATELLITE_MOSAIC_MIN_ZOOM}
        maxNativeZoom={WORLD_SATELLITE_MOSAIC_MAX_ZOOM}
        maxZoom={WORLD_SATELLITE_MOSAIC_MAX_ZOOM}
        zIndex={LAYER_Z_INDEX.SEAMLESS_PHOTO}
        opacity={opacity}
      />
    </>
  );
};
